import { useState, useEffect, useCallback } from 'react';
import { sortByName } from '../../lib/sortByName';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import {
    Plus, KeyRound, MapPin, Clock, Check, X, Loader2, Shield, Calendar,
    Pencil, RotateCcw, Ban, QrCode, Download,
} from 'lucide-react';
import { QRCodeSVG } from 'qrcode.react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import {
    Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../ui/table';
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '../ui/dialog';
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../ui/select';
import { DurationPicker } from '../shared/DurationPicker';
import { stringifyDetail } from '../../lib/utils';
import { parseUtc } from '../../lib/datetime';

const API_BASE = window.location.origin;

const STATUS_STYLES = {
    active: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300',
    expired: 'bg-slate-200 text-slate-700 dark:bg-slate-700 dark:text-slate-300',
    disabled: 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300',
};

export default function ManagerAccessRegistry({ onAction, scope, sessionToken }) {
    const { t } = useTranslation();
    // Prefer manager-session token if passed (works even when user isn't /login-authenticated);
    // fallback to the portal's yulontap_token when this registry is opened from the Admin Portal.
    const portalToken = localStorage.getItem('yulontap_token');
    const authToken = sessionToken || portalToken;
    const useManagerSession = !!sessionToken;

    const [records, setRecords] = useState([]);
    const [locations, setLocations] = useState([]);
    const [loading, setLoading] = useState(false);
    const [createOpen, setCreateOpen] = useState(false);
    const [extendTarget, setExtendTarget] = useState(null);

    // Create form
    const [fName, setFName] = useState('');
    const [fLoc, setFLoc] = useState('');
    const [fPin, setFPin] = useState('');
    const [fPinConfirm, setFPinConfirm] = useState('');
    const [fDurType, setFDurType] = useState('hours');
    const [fDurVal, setFDurVal] = useState(48);
    const [saving, setSaving] = useState(false);

    // Extend form
    const [eDurType, setEDurType] = useState('hours');
    const [eDurVal, setEDurVal] = useState(24);

    // QR generator
    const [qrOpen, setQrOpen] = useState(false);
    const [qrLoc, setQrLoc] = useState('');
    const [qrResult, setQrResult] = useState(null);
    const [generatingQr, setGeneratingQr] = useState(false);

    const headers = useManagerSession
        ? { 'X-Manager-Session': authToken }
        : { Authorization: `Bearer ${authToken}` };

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const res = await fetch(`${API_BASE}/api/manager-access`, { headers });
            if (res.ok) {
                const data = await res.json();
                setRecords(data.records || []);
            }
        } catch { /* silent */ }
        setLoading(false);
    }, [authToken]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => { load(); }, [load]);

    useEffect(() => {
        // When invoked from a manager session (no portal login), use the manager-scoped
        // locations endpoint which authenticates via X-Manager-Session. Otherwise use
        // the portal /api/locations endpoint with the Bearer token.
        const url = useManagerSession
            ? `${API_BASE}/api/manager/locations`
            : `${API_BASE}/api/locations`;
        fetch(url, { headers })
            .then(r => r.ok ? r.json() : [])
            .then(data => {
                const list = Array.isArray(data) ? data : [];
                if (!useManagerSession && scope && !scope.is_super_admin && scope.organization_id) {
                    setLocations(list.filter(l => l.organization_id === scope.organization_id));
                } else {
                    setLocations(sortByName(list));
                }
            })
            .catch(() => {});
    }, [authToken]); // eslint-disable-line react-hooks/exhaustive-deps

    const submitCreate = async () => {
        onAction?.();
        if (fPin !== fPinConfirm) { toast.error(t('manager.pinsDontMatch')); return; }
        if (!/^\d{6}$/.test(fPin)) { toast.error(t('manager.pinHint4or6')); return; }
        if (!fName.trim() || !fLoc) return;
        setSaving(true);
        try {
            const res = await fetch(`${API_BASE}/api/manager-access`, {
                method: 'POST',
                headers: { ...headers, 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    manager_name: fName.trim(),
                    location_id: fLoc,
                    pin: fPin,
                    confirm_pin: fPinConfirm,
                    duration_type: fDurType,
                    duration_value: fDurType === 'permanent' ? null : Number(fDurVal),
                }),
            });
            if (res.ok) {
                toast.success(t('manager.accessCreated'));
                setCreateOpen(false);
                setFName(''); setFLoc(''); setFPin(''); setFPinConfirm('');
                setFDurType('hours'); setFDurVal(48);
                load();
            } else {
                const err = await res.json().catch(() => ({}));
                toast.error(stringifyDetail(err.detail) || t('manager.failed'));
            }
        } catch { toast.error(t('manager.failed')); }
        setSaving(false);
    };

    const doAction = async (id, action, body) => {
        onAction?.();
        try {
            const res = await fetch(`${API_BASE}/api/manager-access/${id}/${action}`, {
                method: 'PUT',
                headers: { ...headers, 'Content-Type': 'application/json' },
                body: body ? JSON.stringify(body) : undefined,
            });
            if (res.ok) {
                toast.success(
                    action === 'disable' ? t('manager.accessDisabled')
                    : action === 'reactivate' ? t('manager.accessReactivated')
                    : t('manager.accessExtended')
                );
                load();
                return true;
            } else {
                const err = await res.json().catch(() => ({}));
                toast.error(stringifyDetail(err.detail) || t('manager.failed'));
            }
        } catch { toast.error(t('manager.failed')); }
        return false;
    };

    const submitExtend = async () => {
        if (!extendTarget) return;
        const ok = await doAction(extendTarget.id, 'extend', {
            duration_type: eDurType,
            duration_value: eDurType === 'permanent' ? null : Number(eDurVal),
        });
        if (ok) setExtendTarget(null);
    };

    const generateQr = async () => {
        if (!qrLoc) return;
        onAction?.();
        setGeneratingQr(true);
        try {
            const res = await fetch(`${API_BASE}/api/manager/qr/generate`, {
                method: 'POST',
                headers: { ...headers, 'Content-Type': 'application/json' },
                body: JSON.stringify({ location_id: qrLoc }),
            });
            if (res.ok) {
                const data = await res.json();
                setQrResult(data);
                toast.success(t('manager.qrGenerated', { date: parseUtc(data.expires_at).toLocaleDateString() }));
            } else {
                const err = await res.json().catch(() => ({}));
                toast.error(stringifyDetail(err.detail) || t('manager.failed'));
            }
        } catch { toast.error(t('manager.failed')); }
        setGeneratingQr(false);
    };

    const qrUrl = qrResult ? `${window.location.origin}/manager/qr/${qrResult.token}` : '';

    const fmtExpiry = (rec) => {
        if (rec.duration_type === 'permanent' || !rec.expires_at) return t('manager.neverExpires');
        return t('manager.expiresOn', { date: parseUtc(rec.expires_at).toLocaleString() });
    };
    const fmtLast = (rec) => rec.last_used_at ? parseUtc(rec.last_used_at).toLocaleString() : t('manager.never');

    return (
        <div className="space-y-4" data-testid="manager-access-registry">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                <h3 className="text-base font-semibold flex items-center gap-2 shrink-0">
                    <Shield className="w-4 h-4 text-fuchsia-500 shrink-0" /> {t('manager.registry')}
                </h3>
                <div className="grid grid-cols-2 sm:flex gap-2 w-full sm:w-auto">
                    <Button
                        onClick={() => { onAction?.(); setCreateOpen(true); }}
                        className="bg-fuchsia-600 hover:bg-fuchsia-700 min-h-[40px] px-3 text-sm"
                        data-testid="mgr-access-create-btn"
                        data-insight-id="neo.workflow.create-access"
                        data-insight-label="Create manager access record"
                    >
                        <Plus className="w-4 h-4 mr-1 shrink-0" />
                        <span className="truncate">{t('manager.createAccess')}</span>
                    </Button>
                    <Button
                        onClick={() => { onAction?.(); setQrOpen(true); setQrResult(null); }}
                        variant="outline"
                        className="border-fuchsia-500/40 text-fuchsia-500 hover:bg-fuchsia-500/10 min-h-[40px] px-3 text-sm"
                        data-testid="mgr-access-qr-btn"
                        data-insight-id="neo.workflow.generate-qr"
                        data-insight-label="Generate NEO entry QR"
                    >
                        <QrCode className="w-4 h-4 mr-1 shrink-0" />
                        <span className="truncate">{t('manager.qrGenerate')}</span>
                    </Button>
                </div>
            </div>

            {loading ? (
                <div className="flex justify-center py-6"><Loader2 className="w-5 h-5 animate-spin" /></div>
            ) : records.length === 0 ? (
                <p className="text-center text-sm text-muted-foreground py-6">{t('manager.noRecords')}</p>
            ) : (
                <div className="overflow-x-auto">
                    <Table>
                        <TableHeader>
                            <TableRow>
                                <TableHead>{t('manager.managerName')}</TableHead>
                                <TableHead>{t('manager.assignedLocation')}</TableHead>
                                <TableHead>Status</TableHead>
                                <TableHead>{t('manager.duration')}</TableHead>
                                <TableHead>{t('manager.createdBy')}</TableHead>
                                <TableHead>{t('manager.lastUsed')}</TableHead>
                                <TableHead className="text-right">{t('vouchers.colAction')}</TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {(() => {
                                // First-seen-per-status indices so each action button class
                                // (extend/disable on first active row, reactivate on first
                                // disabled row) carries its insight-id at least once.
                                let firstActiveIdx = -1;
                                let firstDisabledIdx = -1;
                                records.forEach((r, i) => {
                                    if (firstActiveIdx === -1 && r.status === 'active') firstActiveIdx = i;
                                    if (firstDisabledIdx === -1 && r.status === 'disabled') firstDisabledIdx = i;
                                });
                                return records.map((r, idx) => (
                                <TableRow key={r.id} data-testid={`mgr-access-row-${r.id}`}
                                    data-insight-id={idx === 0 ? "neo.workflow.registry-row" : undefined}
                                    data-insight-label={idx === 0 ? "Manager access record" : undefined}
                                >
                                    <TableCell className="font-medium">{r.manager_name}</TableCell>
                                    <TableCell className="text-sm">{r.location_name || '-'}</TableCell>
                                    <TableCell>
                                        <Badge className={STATUS_STYLES[r.status]} variant="secondary">
                                            {t(`manager.status${r.status.charAt(0).toUpperCase() + r.status.slice(1)}`)}
                                        </Badge>
                                    </TableCell>
                                    <TableCell className="text-xs text-muted-foreground">{fmtExpiry(r)}</TableCell>
                                    <TableCell className="text-xs">{r.created_by_name || '-'}</TableCell>
                                    <TableCell className="text-xs text-muted-foreground">{fmtLast(r)}</TableCell>
                                    <TableCell className="text-right space-x-1">
                                        {r.status === 'active' ? (
                                            <>
                                                <Button size="sm" variant="ghost" onClick={() => { setExtendTarget(r); setEDurType('hours'); setEDurVal(24); }} data-testid={`mgr-access-extend-${r.id}`}
                                                    data-insight-id={idx === firstActiveIdx ? "neo.workflow.extend-access" : undefined}
                                                    data-insight-label={idx === firstActiveIdx ? "Extend access duration" : undefined}
                                                >
                                                    <Pencil className="w-3.5 h-3.5" />
                                                </Button>
                                                <Button size="sm" variant="ghost" onClick={() => doAction(r.id, 'disable')} className="text-red-500 hover:text-red-600" data-testid={`mgr-access-disable-${r.id}`}
                                                    data-insight-id={idx === firstActiveIdx ? "neo.workflow.disable-access" : undefined}
                                                    data-insight-label={idx === firstActiveIdx ? "Disable manager access" : undefined}
                                                >
                                                    <Ban className="w-3.5 h-3.5" />
                                                </Button>
                                            </>
                                        ) : r.status === 'disabled' ? (
                                            <Button size="sm" variant="ghost" onClick={() => doAction(r.id, 'reactivate')} className="text-emerald-600" data-testid={`mgr-access-reactivate-${r.id}`}
                                                data-insight-id={idx === firstDisabledIdx ? "neo.workflow.reactivate-access" : undefined}
                                                data-insight-label={idx === firstDisabledIdx ? "Reactivate disabled access" : undefined}
                                            >
                                                <RotateCcw className="w-3.5 h-3.5" />
                                            </Button>
                                        ) : (
                                            <Button size="sm" variant="ghost" onClick={() => { setExtendTarget(r); setEDurType('hours'); setEDurVal(24); }} data-testid={`mgr-access-extend-expired-${r.id}`}>
                                                <Pencil className="w-3.5 h-3.5" />
                                            </Button>
                                        )}
                                    </TableCell>
                                </TableRow>
                                ));
                            })()}
                        </TableBody>
                    </Table>
                </div>
            )}

            {/* Create dialog */}
            <Dialog open={createOpen} onOpenChange={setCreateOpen}>
                <DialogContent data-testid="mgr-access-create-modal">
                    <DialogHeader>
                        <DialogTitle>{t('manager.createAccess')}</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-3">
                        <div className="space-y-1">
                            <Label className="text-xs">{t('manager.managerName')}</Label>
                            <Input value={fName} onChange={(e) => setFName(e.target.value)} data-testid="mgr-f-name" />
                        </div>
                        <div className="space-y-1">
                            <Label className="text-xs flex items-center gap-1"><MapPin className="w-3 h-3" /> {t('manager.assignedLocation')}</Label>
                            <Select value={fLoc} onValueChange={setFLoc}>
                                <SelectTrigger data-testid="mgr-f-loc"><SelectValue placeholder={t('adminUsers.selectLocation')} /></SelectTrigger>
                                <SelectContent>
                                    {locations.map(l => (<SelectItem key={l.id} value={l.id}>{l.name}</SelectItem>))}
                                </SelectContent>
                            </Select>
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                            <div className="space-y-1">
                                <Label className="text-xs flex items-center gap-1 min-h-[16px]"><KeyRound className="w-3 h-3" /> {t('manager.pin')}</Label>
                                <Input type="password" inputMode="numeric" maxLength={6}
                                    value={fPin} onChange={(e) => setFPin(e.target.value.replace(/\D/g, '').slice(0, 6))}
                                    data-testid="mgr-f-pin" />
                            </div>
                            <div className="space-y-1">
                                <Label className="text-xs flex items-center gap-1 min-h-[16px]"><KeyRound className="w-3 h-3 opacity-0" aria-hidden="true" /> {t('manager.confirmPin')}</Label>
                                <Input type="password" inputMode="numeric" maxLength={6}
                                    value={fPinConfirm} onChange={(e) => setFPinConfirm(e.target.value.replace(/\D/g, '').slice(0, 6))}
                                    data-testid="mgr-f-pin-confirm" />
                            </div>
                        </div>
                        <div className="space-y-1">
                            <Label className="text-xs flex items-center gap-1"><Clock className="w-3 h-3" /> {t('manager.duration')}</Label>
                            <DurationPicker
                                value={{ type: fDurType, value: parseInt(fDurVal) || 24 }}
                                onChange={(next) => {
                                    setFDurType(next.type);
                                    if (next.value != null) setFDurVal(String(next.value));
                                }}
                                testIdPrefix="mgr-f-duration"
                            />
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setCreateOpen(false)} disabled={saving}>{t('manager.cancel')}</Button>
                        <Button onClick={submitCreate} disabled={saving} className="bg-fuchsia-600 hover:bg-fuchsia-700" data-testid="mgr-f-submit">
                            {saving ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Check className="w-4 h-4 mr-1" />}
                            {t('manager.saveAndCreate')}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Extend dialog */}
            <Dialog open={!!extendTarget} onOpenChange={(o) => { if (!o) setExtendTarget(null); }}>
                <DialogContent data-testid="mgr-access-extend-modal">
                    <DialogHeader>
                        <DialogTitle>{t('manager.extend')} — {extendTarget?.manager_name}</DialogTitle>
                        <DialogDescription>{extendTarget?.location_name}</DialogDescription>
                    </DialogHeader>
                    <div className="space-y-2">
                        <Label className="text-xs flex items-center gap-1"><Clock className="w-3 h-3" /> {t('manager.duration')}</Label>
                        <DurationPicker
                            value={{ type: eDurType, value: parseInt(eDurVal) || 1 }}
                            onChange={(next) => {
                                setEDurType(next.type);
                                if (next.value != null) setEDurVal(String(next.value));
                            }}
                            testIdPrefix="mgr-ext-duration"
                        />
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setExtendTarget(null)}>{t('manager.cancel')}</Button>
                        <Button onClick={submitExtend} className="bg-fuchsia-600 hover:bg-fuchsia-700" data-testid="mgr-ext-submit">
                            <Calendar className="w-4 h-4 mr-1" /> {t('manager.extend')}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* QR Generator Dialog */}
            <Dialog open={qrOpen} onOpenChange={(o) => { setQrOpen(o); if (!o) { setQrResult(null); setQrLoc(''); } }}>
                <DialogContent data-testid="mgr-qr-modal">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            <QrCode className="w-5 h-5 text-fuchsia-500" /> {t('manager.qrGenerate')}
                        </DialogTitle>
                        <DialogDescription>{t('manager.qrValidFor')}</DialogDescription>
                    </DialogHeader>
                    {!qrResult ? (
                        <div className="space-y-3">
                            <div className="space-y-1">
                                <Label className="text-xs flex items-center gap-1"><MapPin className="w-3 h-3" /> {t('manager.assignedLocation')}</Label>
                                <Select value={qrLoc} onValueChange={setQrLoc}>
                                    <SelectTrigger data-testid="mgr-qr-loc"><SelectValue placeholder={t('adminUsers.selectLocation')} /></SelectTrigger>
                                    <SelectContent>
                                        {locations.map(l => (<SelectItem key={l.id} value={l.id}>{l.name}</SelectItem>))}
                                    </SelectContent>
                                </Select>
                            </div>
                        </div>
                    ) : (
                        <div className="flex flex-col items-center gap-3 py-3" data-testid="mgr-qr-output">
                            <div className="bg-white p-4 rounded-lg">
                                <QRCodeSVG value={qrUrl} size={220} level="H" />
                            </div>
                            <p className="text-xs text-muted-foreground text-center break-all">{qrUrl}</p>
                            <p className="text-xs text-muted-foreground">
                                {t('manager.expiresOn', { date: parseUtc(qrResult.expires_at).toLocaleString() })}
                            </p>
                        </div>
                    )}
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setQrOpen(false)}>{t('manager.cancel')}</Button>
                        {!qrResult ? (
                            <Button onClick={generateQr} disabled={!qrLoc || generatingQr} className="bg-fuchsia-600 hover:bg-fuchsia-700" data-testid="mgr-qr-generate-submit">
                                {generatingQr ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <QrCode className="w-4 h-4 mr-1" />}
                                {t('manager.qrGenerate')}
                            </Button>
                        ) : (
                            <Button
                                onClick={() => {
                                    // Trigger download by creating a temporary canvas from the SVG
                                    const svg = document.querySelector('[data-testid="mgr-qr-output"] svg');
                                    if (!svg) return;
                                    const serialized = new XMLSerializer().serializeToString(svg);
                                    const blob = new Blob([serialized], { type: 'image/svg+xml' });
                                    const url = URL.createObjectURL(blob);
                                    const a = document.createElement('a');
                                    a.href = url; a.download = 'manager-qr.svg';
                                    document.body.appendChild(a); a.click(); document.body.removeChild(a);
                                    URL.revokeObjectURL(url);
                                }}
                                className="bg-emerald-600 hover:bg-emerald-700"
                                data-testid="mgr-qr-download"
                            >
                                <Download className="w-4 h-4 mr-1" /> {t('manager.qrDownload')}
                            </Button>
                        )}
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
